/** Default limits for client-side image preparation before upload. */
export const IMAGE_UPLOAD_LIMITS = {
  /** Longest edge in pixels. */
  maxEdge: 1920,
  /** Target max file size after encode (~900 KB). */
  maxBytes: 900 * 1024,
  initialQuality: 0.82,
  minQuality: 0.5,
  qualityStep: 0.08,
  outputMime: 'image/jpeg' as const,
} as const;

/** Cover / hero images displayed at 16:9. */
export const COVER_IMAGE_ASPECT = 16 / 9;

export type PrepareImageForUploadOptions = {
  maxEdge?: number;
  maxBytes?: number;
  initialQuality?: number;
  minQuality?: number;
  qualityStep?: number;
  /** Defaults to JPEG for size; use image/webp when needed. */
  outputMime?: 'image/jpeg' | 'image/webp';
  fileName?: string;
  /**
   * Optional silent center-crop (e.g. non-interactive uploads).
   * Interactive crop UIs must not pass this — that would re-crop the selection.
   */
  aspectRatio?: number;
};

export class PrepareImageError extends Error {
  readonly code:
    | 'IMAGE_DECODE_FAILED'
    | 'IMAGE_ENCODE_FAILED'
    | 'IMAGE_TOO_LARGE';

  constructor(code: PrepareImageError['code'], message: string) {
    super(message);
    this.name = 'PrepareImageError';
    this.code = code;
  }
}

export function isRasterImageFile(file: File): boolean {
  if (!file.type.startsWith('image/')) {
    return false;
  }
  return file.type !== 'image/svg+xml';
}

export function resolvePreparedImageFileName(
  originalName: string,
  mime: string
): string {
  const base = originalName.replace(/\.[^.]+$/, '').trim() || 'image';
  const ext = mime === 'image/webp' ? 'webp' : 'jpg';
  return `${base}.${ext}`;
}

/** Exported for unit tests — center crop source rectangle. */
export function resolveCenterCropSourceRect(
  width: number,
  height: number,
  aspectRatio?: number
): { sx: number; sy: number; sw: number; sh: number } {
  if (!aspectRatio || aspectRatio <= 0) {
    return { sx: 0, sy: 0, sw: width, sh: height };
  }

  const sourceAspect = width / height;
  if (sourceAspect > aspectRatio) {
    const sw = height * aspectRatio;
    return { sx: (width - sw) / 2, sy: 0, sw, sh: height };
  }

  const sh = width / aspectRatio;
  return { sx: 0, sy: (height - sh) / 2, sw: width, sh };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(
          new PrepareImageError('IMAGE_ENCODE_FAILED', 'Failed to encode image')
        );
      },
      mime,
      quality
    );
  });
}

async function loadImageElement(source: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(source);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(
          new PrepareImageError('IMAGE_DECODE_FAILED', 'Failed to decode image')
        );
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function computeDrawSize(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height)),
    };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Resizes / optionally center-crops and re-encodes a raster image so uploads stay
 * within size limits — no user-facing editor.
 */
export async function prepareImageForUpload(
  source: File | Blob,
  options: PrepareImageForUploadOptions = {}
): Promise<File> {
  const maxEdge = options.maxEdge ?? IMAGE_UPLOAD_LIMITS.maxEdge;
  const maxBytes = options.maxBytes ?? IMAGE_UPLOAD_LIMITS.maxBytes;
  const initialQuality =
    options.initialQuality ?? IMAGE_UPLOAD_LIMITS.initialQuality;
  const minQuality = options.minQuality ?? IMAGE_UPLOAD_LIMITS.minQuality;
  const qualityStep = options.qualityStep ?? IMAGE_UPLOAD_LIMITS.qualityStep;
  const outputMime = options.outputMime ?? IMAGE_UPLOAD_LIMITS.outputMime;
  const originalName =
    options.fileName ??
    (source instanceof File ? source.name : 'image.jpg');

  const image = await loadImageElement(source);
  const sourceRect = resolveCenterCropSourceRect(
    image.naturalWidth,
    image.naturalHeight,
    options.aspectRatio
  );
  const draw = computeDrawSize(sourceRect.sw, sourceRect.sh, maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = draw.width;
  canvas.height = draw.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new PrepareImageError('IMAGE_ENCODE_FAILED', 'Canvas is not supported');
  }

  if (outputMime === 'image/jpeg') {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, draw.width, draw.height);
  }

  context.drawImage(
    image,
    sourceRect.sx,
    sourceRect.sy,
    sourceRect.sw,
    sourceRect.sh,
    0,
    0,
    draw.width,
    draw.height
  );

  let quality = initialQuality;
  let bestBlob: Blob | null = null;

  while (quality >= minQuality - 1e-9) {
    const blob = await canvasToBlob(canvas, outputMime, quality);
    bestBlob = blob;
    if (blob.size <= maxBytes) {
      return new File(
        [blob],
        resolvePreparedImageFileName(originalName, outputMime),
        {
          type: outputMime,
          lastModified: Date.now(),
        }
      );
    }
    quality = Math.round((quality - qualityStep) * 100) / 100;
  }

  if (bestBlob && bestBlob.size <= maxBytes) {
    return new File(
      [bestBlob],
      resolvePreparedImageFileName(originalName, outputMime),
      {
        type: outputMime,
        lastModified: Date.now(),
      }
    );
  }

  throw new PrepareImageError(
    'IMAGE_TOO_LARGE',
    `Image could not be compressed under ${maxBytes} bytes`
  );
}
